import { geminiModel } from '../lib/firebase';
import type { Game, GameOutcome, ShotType, GameSituation } from '../types';
import { getCurrentPromptGenerator } from '../prompts';

export interface VideoAnalysisResult {
  opponent: string;
  competitionType: Game['competitionType'];
  shotsFaced: number | null;
  saves: number | null;
  outcome: GameOutcome;
  shots: Array<{
    shotType: ShotType;
    gameSituation: GameSituation;
    result: 'save' | 'goal';
    shotOriginX: number;
    shotOriginY: number;
    shotDestinationX: number;
    shotDestinationY: number;
    // New timestamp and analysis fields
    clipStartTime: string; // MM:SS format
    clipEndTime: string;   // MM:SS format
    analysis: {
      goalieTechnique: string;
      whatWentWell: string;
      areasForImprovement: string;
      keyMoment: string;
      coachingTip: string;
    };
  }>;
}

export interface VideoAnalysisError {
  message: string;
  code: string;
}

// Use the current prompt generator from the centralized prompts module
const createAnalysisPrompt = getCurrentPromptGenerator();

export class VideoAnalysisService {
  async analyzeVideo(videoSource: string, language: 'en' | 'fr' = 'en'): Promise<VideoAnalysisResult> {
    try {
      // Validate video source (URL or file URL)
      if (!videoSource) {
        throw new Error('Please provide a video URL or upload a video file');
      }

      const prompt = createAnalysisPrompt(language);
      
      // Convert video to base64 for Firebase AI Logic API
      const videoData = await this.prepareVideoData(videoSource);
      
      // Call Gemini API with video content in proper inlineData format
      const result = await geminiModel.generateContent([
        prompt,
        videoData
      ]);
      const response = await result.response;
      const text = response.text();

      console.log('Raw AI response:', text);

      // Parse JSON response
      let analysisData: VideoAnalysisResult;
      try {
        // First try to parse the entire response as JSON
        try {
          analysisData = JSON.parse(text);
        } catch {
          // If that fails, try to extract JSON from markdown code blocks
          const jsonMatch = text.match(/```(?:json)?\s*([\s\S]*?)\s*```/) || 
                           text.match(/\{[\s\S]*\}/);
          
          if (!jsonMatch) {
            console.error('No valid JSON found in response. Full response:', text);
            throw new Error('No valid JSON found in response');
          }
          
          // Use the captured group if it exists, otherwise use the full match
          const jsonText = jsonMatch[1] || jsonMatch[0];
          analysisData = JSON.parse(jsonText);
        }
      } catch (parseError) {
        console.error('Failed to parse AI response:', text);
        console.error('Parse error:', parseError);
        throw new Error('Failed to parse video analysis results. Please try again.');
      }

      // Validate required fields
      if (!analysisData.opponent || !analysisData.competitionType || !analysisData.shots) {
        throw new Error('Incomplete analysis results received');
      }

      // Log the actual timestamps we received
      if (analysisData.shots) {
        console.log('Received shots with timestamps:', analysisData.shots.map(shot => ({
          clipStartTime: shot.clipStartTime,
          clipEndTime: shot.clipEndTime,
          shotType: shot.shotType
        })));
      }

      return analysisData;
    } catch (error) {
      console.error('Video analysis error:', error);
      
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('Failed to analyze video. Please check the URL and try again.');
    }
  }

  private async prepareVideoData(videoSource: string): Promise<{ inlineData: { data: string; mimeType: string } }> {
    try {
      // Fetch the video data
      const response = await fetch(videoSource);
      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status} ${response.statusText}`);
      }

      // Get the video data as array buffer
      const arrayBuffer = await response.arrayBuffer();
      
      // Convert to base64 using a more efficient method for large files
      const base64 = this.arrayBufferToBase64(arrayBuffer);
      
      // Determine MIME type from response headers or URL extension
      let mimeType = response.headers.get('content-type') || 'video/mp4';
      
      // If no content-type header, try to guess from URL extension
      if (!mimeType || mimeType === 'application/octet-stream') {
        const extension = videoSource.split('.').pop()?.toLowerCase();
        switch (extension) {
          case 'mp4':
            mimeType = 'video/mp4';
            break;
          case 'mov':
            mimeType = 'video/quicktime';
            break;
          case 'avi':
            mimeType = 'video/x-msvideo';
            break;
          case 'webm':
            mimeType = 'video/webm';
            break;
          case 'mkv':
            mimeType = 'video/x-matroska';
            break;
          default:
            mimeType = 'video/mp4';
        }
      }

      return {
        inlineData: {
          data: base64,
          mimeType: mimeType
        }
      };
    } catch (error) {
      console.error('Error preparing video data:', error);
      throw new Error(`Failed to process video file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private arrayBufferToBase64(arrayBuffer: ArrayBuffer): string {
    const uint8Array = new Uint8Array(arrayBuffer);
    const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
    let binaryString = '';
    
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      binaryString += String.fromCharCode(...chunk);
    }
    
    return btoa(binaryString);
  }

  private isValidVideoSource(source: string): boolean {
    try {
      const urlObj = new URL(source);
      const hostname = urlObj.hostname.toLowerCase();
      
      // Support Firebase Storage URLs
      if (hostname.includes('firebasestorage.googleapis.com')) {
        return true;
      }
      
      // Support common video platforms
      const supportedPlatforms = [
        'youtube.com',
        'youtu.be',
        'vimeo.com',
        'drive.google.com',
        'dropbox.com',
        'streamable.com',
        'twitch.tv',
        'clips.twitch.tv'
      ];
      
      return supportedPlatforms.some(platform => 
        hostname.includes(platform)
      ) || 
      // Also support direct video file URLs
      /\.(mp4|mov|avi|wmv|flv|webm|mkv)$/i.test(urlObj.pathname);
    } catch {
      return false;
    }
  }
}

export const videoAnalysisService = new VideoAnalysisService();